import { useAuthStore, parseJwtClaims } from '@/stores/authStore'
import { authApi } from '@/api/clients/authApi'
import { isEmbedded } from '@/lib/embed'
import { requestTokenFromParent, markEmbedSessionLost } from '@/lib/embedBridge'
import type { ApiResponse } from '@/api/types'

// In development: relative paths → Vite dev proxy → backend.
// In production: set VITE_BASE_URL to the backend origin when the frontend and
// API are on different origins. Leave empty for same-origin deployments.
const BASE = import.meta.env.VITE_BASE_URL ?? ''

// ── Token refresh mutex ───────────────────────────────────────────────────────
// Prevents multiple concurrent 401 responses from each triggering their own
// refresh request. All callers share the same in-flight promise.

let refreshPromise: Promise<string> | null = null

async function getFreshToken(): Promise<string> {
  // Embedded: the partner parent owns refreshing — ask it, never redirect the frame to /login.
  if (isEmbedded()) {
    if (!refreshPromise) {
      refreshPromise = requestTokenFromParent()
        .catch((err) => {
          markEmbedSessionLost()
          throw err
        })
        .finally(() => {
          refreshPromise = null
        })
    }
    return refreshPromise
  }

  const store = useAuthStore.getState()
  if (!store.refreshToken) {
    store.clearSession()
    window.location.href = `${import.meta.env.BASE_URL}login`
    throw new Error('No refresh token available')
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      const data = await authApi.refresh(store.refreshToken!)
      const claims = parseJwtClaims(data.access_token)
      store.setSession({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: data.expires_at,
        userId: claims.uid,
        orgId: claims.oid,
        role: claims.role,
      })
      return data.access_token
    })()
      .catch((err) => {
        store.clearSession()
        window.location.href = `${import.meta.env.BASE_URL}login`
        throw err
      })
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

// ── Errors ────────────────────────────────────────────────────────────────────
// Carries the HTTP status so callers can branch on it (e.g. 404 → not-found UI).

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(`[${status}] ${message}`)
    this.name = 'ApiError'
  }
}

export const isNotFound = (err: unknown): boolean => err instanceof ApiError && err.status === 404

// ── Base HTTP client ──────────────────────────────────────────────────────────
// Unwraps the { success, data, error } envelope and returns `data`.

async function unwrap<T>(resp: Response): Promise<T> {
  if (resp.status === 204) return undefined as T
  const body = (await resp.json().catch(() => ({}))) as ApiResponse<T>
  if (!resp.ok || body.success === false) {
    throw new ApiError(resp.status, body?.error?.message ?? resp.statusText)
  }
  // Envelope shape is { success, data }. When present, return data verbatim — even
  // if null (empty list serializes data:null; must NOT fall back to the envelope).
  // Only unwrap-to-body when there is no envelope at all.
  return (body && typeof body === 'object' && 'data' in body ? body.data : body) as T
}

// List endpoints serialize an empty result as data:null — coerce to [] so callers
// get a real array (empty states render, .map/.length are always safe).
export async function requestList<T>(endpoint: string, options: RequestInit = {}): Promise<T[]> {
  const r = await request<T[] | null>(endpoint, options)
  return Array.isArray(r) ? r : []
}

export async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const store = useAuthStore.getState()
  const headers: Record<string, string> = {
    // Skip Content-Type for FormData — let the browser set the multipart boundary.
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string>),
  }
  if (store.accessToken) headers['Authorization'] = `Bearer ${store.accessToken}`

  const response = await fetch(`${BASE}${endpoint}`, { ...options, headers })

  if (response.status === 401) {
    const freshToken = await getFreshToken()
    const retryHeaders = { ...headers, Authorization: `Bearer ${freshToken}` }
    const retry = await fetch(`${BASE}${endpoint}`, { ...options, headers: retryHeaders })
    return unwrap<T>(retry)
  }

  return unwrap<T>(response)
}

// Raw text/stream fetch (executive report, SSE chat) — bypasses envelope unwrap.
export async function rawRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const store = useAuthStore.getState()
  const headers: Record<string, string> = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string>),
  }
  if (store.accessToken) headers['Authorization'] = `Bearer ${store.accessToken}`

  const response = await fetch(`${BASE}${endpoint}`, { ...options, headers })
  if (response.status === 401) {
    const freshToken = await getFreshToken()
    const retryHeaders = { ...headers, Authorization: `Bearer ${freshToken}` }
    return fetch(`${BASE}${endpoint}`, { ...options, headers: retryHeaders })
  }
  return response
}

export function buildQuery(params?: Record<string, unknown>): string {
  if (!params) return ''
  const q = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&')
  return q ? `?${q}` : ''
}

// All qtz-risk endpoints are org-scoped.
export const orgBase = (orgId: string) => `/api/v1/organizations/${orgId}`
